import { QueryClient, environmentManager } from "@tanstack/react-query";

/**
 * Builds a QueryClient with app-wide defaults for server-state caching.
 * A 1-minute `staleTime` dedupes bursts of identical fetches; a single retry
 * keeps transient network blips from surfacing as errors.
 */
export const makeQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 60_000,
      },
    },
  });

let browserQueryClient: QueryClient | undefined;

/**
 * Returns the app's QueryClient — the server-state cache.
 *
 * In the browser it returns a module-level singleton. That singleton is what
 * lets plain `.ts` service files read and write the cache OUTSIDE React
 * (`getQueryClient().setQueryData(...)` / `.getQueryData(...)`) — the "update
 * state outside components" ergonomic, with no Context and no component tree.
 *
 * On the server it always returns a fresh client, so server-rendered requests
 * never leak cache state across users. (This app fetches the profile
 * client-side, so services only ever touch the browser singleton.)
 */
export const getQueryClient = (): QueryClient => {
  if (environmentManager.isServer()) {
    return makeQueryClient();
  }

  browserQueryClient ??= makeQueryClient();
  return browserQueryClient;
};
